import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import {
  ClipboardCheck,
  HandCoins,
  MessageSquare,
  Smartphone,
  Users,
  Wallet,
} from 'lucide-react';

import { Button } from '@/components/ui/button';

import { LANDING_APP, LANDING_DASHBOARD } from './landing-assets';

const FEATURE_ICONS = [
  { Icon: Users, labelKey: 'benefits.tabs.people.title' },
  { Icon: MessageSquare, labelKey: 'benefits.tabs.messaging.title' },
  { Icon: Wallet, labelKey: 'benefits.tabs.finances.title' },
  { Icon: ClipboardCheck, labelKey: 'benefits.tabs.attendance.title' },
  { Icon: HandCoins, labelKey: 'benefits.tabs.giving.title' },
  { Icon: Smartphone, labelKey: 'benefits.tabs.app.title' },
] as const;

function BenefitConsoleMockup({ alt, url }: Readonly<{ alt: string; url: string }>) {
  return (
    <div className="landing-benefits-mockup w-full rounded-[1.1rem] border border-white/25 bg-white/12 p-2 shadow-e4 sm:p-2.5">
      <div className="overflow-hidden rounded-lg border border-white/20 bg-white shadow-e3">
        <div className="flex items-center gap-1.5 border-b border-slate-200/90 bg-slate-50 px-3 py-2">
          <span className="size-2 rounded-full bg-red-400/90" aria-hidden />
          <span className="size-2 rounded-full bg-amber-400/90" aria-hidden />
          <span className="size-2 rounded-full bg-emerald-400/90" aria-hidden />
          <span className="ml-2 truncate text-caption text-fg-secondary">{url}</span>
        </div>
        <div className="aspect-[2048/1070] bg-white">
          <img
            src={LANDING_DASHBOARD}
            alt={alt}
            width={2048}
            height={1070}
            className="h-full w-full object-cover object-left-top"
            decoding="async"
            fetchPriority="high"
          />
        </div>
      </div>
      <div
        className="mx-auto -mt-px h-3 w-[88%] rounded-b-xl border border-t-0 border-white/20 bg-white/15"
        aria-hidden
      />
    </div>
  );
}

function BenefitAppMockup({ alt }: Readonly<{ alt: string }>) {
  return (
    <div className="landing-benefits-app-mockup w-full max-w-[11.5rem] rounded-[1.85rem] border border-white/30 bg-slate-950/90 p-1.5 shadow-e4 sm:max-w-[12.75rem] lg:max-w-[14.5rem]">
      <div className="overflow-hidden rounded-[1.45rem] border border-white/10 bg-black">
        <div className="flex justify-center bg-black px-4 pb-1 pt-2" aria-hidden>
          <span className="h-1.5 w-14 rounded-full bg-slate-800" />
        </div>
        <div className="aspect-[1206/2622] bg-white">
          <img
            src={LANDING_APP}
            alt={alt}
            width={1206}
            height={2622}
            className="h-full w-full object-cover object-top"
            decoding="async"
            loading="lazy"
          />
        </div>
      </div>
    </div>
  );
}

function BenefitPreviewMockups({
  consoleAlt,
  appAlt,
  url,
}: Readonly<{ consoleAlt: string; appAlt: string; url: string }>) {
  return (
    <div className="mx-auto w-full max-w-5xl">
      <div className="flex flex-col items-center gap-8 lg:relative lg:items-stretch lg:pb-10">
        <BenefitConsoleMockup alt={consoleAlt} url={url} />
        <div className="lg:absolute lg:bottom-0 lg:right-0 lg:w-[30%] lg:max-w-[15rem] lg:translate-x-1/4 xl:max-w-[16.5rem]">
          <BenefitAppMockup alt={appAlt} />
        </div>
      </div>
    </div>
  );
}

export const LandingBenefitsSection = () => {
  const { t } = useTranslation('landing');

  return (
    <section
      id="benefits"
      className="landing-benefits-hero scroll-mt-20 pb-16 pt-16 sm:pb-20 sm:pt-20 lg:pb-24 lg:pt-28"
      aria-labelledby="landing-hero-heading"
    >
      <div className="mx-auto flex max-w-6xl flex-col items-center px-4 sm:px-6 lg:px-8">
        <div className="mx-auto w-full max-w-3xl text-center">
          <h1
            id="landing-hero-heading"
            className="landing-hero-headline landing-animate-2 text-balance"
          >
            {t('hero.title')}
          </h1>
          <p className="landing-hero-subtitle landing-animate-3 mx-auto mt-4 max-w-2xl text-pretty">
            {t('hero.subtitle')}
          </p>
          <div className="landing-animate-3 landing-cta-stack mt-8">
            <Button
              size="lg"
              variant="outline"
              className="landing-cta-primary-ghost !text-on-brand shadow-none w-full hover:!text-on-brand sm:w-auto"
              asChild
            >
              <Link to="/register">{t('hero.primaryCta')}</Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="landing-cta-secondary landing-cta-secondary-hero shadow-none w-full sm:w-auto"
              asChild
            >
              <Link to="/demo">{t('hero.demoCta')}</Link>
            </Button>
          </div>
        </div>

        <ul
          aria-label={t('benefits.featureIconsAria')}
          className="mt-12 flex w-full max-w-5xl gap-1 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] sm:mt-14 sm:gap-2 lg:mt-16 [&::-webkit-scrollbar]:hidden"
        >
          {FEATURE_ICONS.map(({ Icon, labelKey }) => (
            <li
              key={labelKey}
              className="flex min-w-[5.25rem] flex-1 flex-col items-center gap-2 px-2 py-3 text-center sm:min-w-0 sm:px-3"
            >
              <span
                className="flex h-12 w-12 items-center justify-center rounded-full border border-transparent bg-white/10 text-on-brand/90 sm:h-14 sm:w-14"
                aria-hidden
              >
                <Icon className="size-5 stroke-[1.6] sm:size-[22px]" />
              </span>
              <span className="text-body-sm font-semibold leading-snug text-on-brand">{t(labelKey)}</span>
            </li>
          ))}
        </ul>

        <div className="mt-8 w-full sm:mt-10">
          <BenefitPreviewMockups
            consoleAlt={t('benefits.dashboardAlt')}
            appAlt={t('benefits.appAlt')}
            url={t('benefits.mockupUrl')}
          />
        </div>
      </div>
    </section>
  );
};
